// Bank Reconciliation Agent UI (Polished)
// Additions: summary strip, GL vs Bank trend chart, timing/missing bucket donut,
// drill-down actions on match candidates, export button.
import React, { useState, useEffect } from 'react';
import {
    Box, Button, TextField, Typography, Chip, Stack, Paper, Grid, Divider,
    Tooltip, IconButton,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import LinkIcon from '@mui/icons-material/Link';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AgentPageShell from './AgentPageShell';
import {
    SummaryGrid, SectionHeader, DrillTable, EmptyState, LoadingBlock,
    SimpleBarChart, DonutChart, TrendChart, fmtMoney, fmtDate, CHART_COLORS,
    RadialGauge,
} from './AgentUIKit';
import {
    reconMatch, reconOneToMany, reconManyToOne, reconTiming, reconMissing, reconReport,
} from './api';

const TABS = ['Single Match', 'One→Many', 'Many→One', 'Timing Diffs', 'Missing Entries', 'Period Report'];

export default function ReconciliationAgent() {
    const [tab, setTab] = useState(0);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const [matchId, setMatchId] = useState('');
    const [matchResult, setMatchResult] = useState(null);

    const [o2mId, setO2mId] = useState('');
    const [o2mResult, setO2mResult] = useState(null);

    const [m2oId, setM2oId] = useState('');
    const [m2oResult, setM2oResult] = useState(null);

    const [timing, setTiming] = useState(null);
    const [missing, setMissing] = useState(null);
    const [missingMinAge, setMissingMinAge] = useState(5);
    const [report, setReport] = useState(null);
    const [periodStart, setPeriodStart] = useState('');
    const [periodEnd, setPeriodEnd] = useState('');

    const run = async (fn) => {
        setLoading(true); setError(null);
        try { return await fn(); }
        catch (e) { setError(e?.response?.data?.message || e.message); }
        finally { setLoading(false); }
    };

    const loadTiming = () => run(() => reconTiming()).then(r => r?.data?.success && setTiming(r.data.data));
    const loadMissing = (age = missingMinAge) => run(() => reconMissing(age)).then(r => r?.data?.success && setMissing(r.data.data));

    useEffect(() => {
        if (tab === 3 && !timing) loadTiming();
        if (tab === 4 && !missing) loadMissing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    const summaryCards = [
        { label: 'Timing Items', value: timing ? (timing.outstanding_checks_estimate + timing.deposits_in_transit_estimate) : '—', accent: 'warning' },
        { label: 'Unmatched Bank', value: timing?.unmatched_bank_items ?? '—', accent: 'danger' },
        { label: 'Missing GL Entries', value: missing?.missing_count ?? '—', accent: 'warning' },
        { label: 'Last Period Status', value: report?.status || '—', accent: report?.status === 'RECONCILED' ? 'success' : 'warning' },
    ];

    // Build donut for report
    const buildReportDonut = () => {
        if (!report) return [];
        return [
            { name: 'GL Lines', value: report.gl_summary?.lines || 0 },
            { name: 'Bank Inflows', value: report.bank_summary?.total_inflows || 0 },
            { name: 'Bank Outflows', value: report.bank_summary?.total_outflows || 0 },
        ];
    };

    // Build bar chart for timing breakdown
    const timingBarData = timing ? [
        { bucket: 'Outstanding Checks', count: timing.outstanding_checks_estimate },
        { bucket: 'Deposits in Transit', count: timing.deposits_in_transit_estimate },
        { bucket: 'Unmatched Bank Items', count: timing.unmatched_bank_items },
    ] : [];

    const renderTab = () => {
        switch (tab) {
            case 0:
                return (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Single Bank ↔ GL Match" subtitle="Multi-signal matcher: amount, date, reference, description" />
                        <Stack direction="row" spacing={1}>
                            <TextField size="small" label="Bank Transaction ID" value={matchId}
                                onChange={e => setMatchId(e.target.value)} sx={{ width: 220 }} />
                            <Button size="small" variant="contained" startIcon={<PlayArrowIcon />}
                                disabled={!matchId || loading}
                                onClick={async () => {
                                    const r = await run(() => reconMatch(parseInt(matchId)));
                                    if (r?.data?.success) setMatchResult(r.data.data);
                                }}>Find Matches</Button>
                        </Stack>
                        {matchResult && (
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                    <Chip size="small" label={`Best Score: ${(matchResult.best_score * 100).toFixed(0)}%`} color="primary" />
                                    <Chip size="small" color={
                                        matchResult.action === 'auto_match' ? 'success' :
                                        matchResult.action === 'suggest' ? 'warning' : 'default'
                                    } label={matchResult.action?.toUpperCase()} />
                                </Stack>
                                <DrillTable
                                    columns={[
                                        { key: 'journal_line_id', label: 'JL' },
                                        { key: 'entry_number', label: 'Entry #' },
                                        { key: 'entry_date', label: 'Date' },
                                        { key: 'amount', label: 'Amount', align: 'right', render: r => fmtMoney(r.amount) },
                                        { key: 'score', label: 'Score', align: 'right', render: r => `${(r.score * 100).toFixed(0)}%` },
                                        { key: 'action', label: '', render: () => (
                                            <Tooltip title="Link this match">
                                                <IconButton size="small" color="primary"><LinkIcon fontSize="small" /></IconButton>
                                            </Tooltip>
                                        )},
                                    ]}
                                    rows={matchResult.candidates || []}
                                    rowKeyField="journal_line_id"
                                />
                            </Paper>
                        )}
                    </Stack>
                );
            case 1:
                return (
                    <Stack spacing={1}>
                        <SectionHeader title="One Bank Txn → Many GL Lines" subtitle="E.g. a single bank deposit = sum of multiple customer payments" />
                        <Stack direction="row" spacing={1}>
                            <TextField size="small" label="Bank Txn ID" value={o2mId}
                                onChange={e => setO2mId(e.target.value)} sx={{ width: 200 }} />
                            <Button size="small" variant="contained" startIcon={<PlayArrowIcon />}
                                disabled={!o2mId || loading}
                                onClick={async () => {
                                    const r = await run(() => reconOneToMany(parseInt(o2mId)));
                                    if (r?.data?.success) setO2mResult(r.data.data);
                                }}>Find Combination</Button>
                        </Stack>
                        {o2mResult && (
                            <SummaryGrid cards={[
                                { label: 'Match Status', value: o2mResult.matched ? 'FOUND' : 'NONE', accent: o2mResult.matched ? 'success' : 'neutral' },
                                { label: 'Target', value: fmtMoney(o2mResult.target_amount), accent: 'info' },
                                { label: 'Sum of Matches', value: fmtMoney(o2mResult.sum_of_matches), accent: 'primary' },
                                { label: 'Entries Matched', value: o2mResult.match_count, accent: 'secondary' },
                            ]} />
                        )}
                    </Stack>
                );
            case 2:
                return (
                    <Stack spacing={1}>
                        <SectionHeader title="Many Bank Txns → One GL Line" subtitle="E.g. a large payment split into multiple ACH batches" />
                        <Stack direction="row" spacing={1}>
                            <TextField size="small" label="Journal Line ID" value={m2oId}
                                onChange={e => setM2oId(e.target.value)} sx={{ width: 200 }} />
                            <Button size="small" variant="contained" startIcon={<PlayArrowIcon />}
                                disabled={!m2oId || loading}
                                onClick={async () => {
                                    const r = await run(() => reconManyToOne(parseInt(m2oId)));
                                    if (r?.data?.success) setM2oResult(r.data.data);
                                }}>Find Combination</Button>
                        </Stack>
                        {m2oResult && (
                            <SummaryGrid cards={[
                                { label: 'Match Status', value: m2oResult.matched ? 'FOUND' : 'NONE', accent: m2oResult.matched ? 'success' : 'neutral' },
                                { label: 'Target', value: fmtMoney(m2oResult.target_amount), accent: 'info' },
                                { label: 'Sum of Matches', value: fmtMoney(m2oResult.sum_of_matches), accent: 'primary' },
                            ]} />
                        )}
                    </Stack>
                );
            case 3:
                return timing ? (
                    <Stack spacing={1.5}>
                        <SectionHeader
                            title="Timing Differences"
                            subtitle="Transactions posted on one side but not yet cleared on the other"
                            actions={<IconButton size="small" onClick={loadTiming}><RefreshIcon fontSize="small" /></IconButton>}
                        />
                        <SummaryGrid cards={[
                            { label: 'Outstanding Checks', value: timing.outstanding_checks_estimate, accent: 'warning' },
                            { label: 'Deposits in Transit', value: timing.deposits_in_transit_estimate, accent: 'info' },
                            { label: 'Unmatched Bank', value: timing.unmatched_bank_items, accent: 'danger' },
                            { label: 'Total Timing Items', value: (timing.outstanding_checks_estimate + timing.deposits_in_transit_estimate + timing.unmatched_bank_items), accent: 'primary' },
                        ]} />
                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                            <Typography variant="caption" fontWeight={600}>Timing Breakdown</Typography>
                            <SimpleBarChart
                                data={timingBarData}
                                xKey="bucket"
                                series={[{ key: 'count', label: 'Count', color: CHART_COLORS.warning }]}
                                height={220}
                                showLegend={false}
                            />
                        </Paper>
                    </Stack>
                ) : <LoadingBlock rows={3} />;
            case 4:
                return (
                    <Stack spacing={1}>
                        <SectionHeader
                            title="Missing GL Entries"
                            subtitle="Bank transactions that have no corresponding GL entry after min age"
                            actions={
                                <Stack direction="row" spacing={1}>
                                    <TextField size="small" label="Min age (days)" type="number" value={missingMinAge}
                                        onChange={e => setMissingMinAge(parseInt(e.target.value) || 5)} sx={{ width: 140 }} />
                                    <Button size="small" variant="outlined" onClick={() => { setMissing(null); loadMissing(missingMinAge); }}>Refresh</Button>
                                </Stack>
                            }
                        />
                        {missing ? (
                            <>
                                <Typography variant="caption">{missing.missing_count} items found</Typography>
                                <DrillTable
                                    columns={[
                                        { key: 'transaction_id', label: 'Txn ID' },
                                        { key: 'date', label: 'Date', render: r => fmtDate(r.date) },
                                        { key: 'description', label: 'Description', render: r => r.description || r.merchant },
                                        { key: 'amount', label: 'Amount', align: 'right', render: r => fmtMoney(r.amount) },
                                        { key: 'suggested_category', label: 'Suggested' },
                                        { key: 'age_days', label: 'Age', align: 'right', render: r => `${r.age_days}d` },
                                        { key: 'action', label: '', render: () => (
                                            <Tooltip title="Create suggested GL entry">
                                                <Button size="small" variant="outlined">Post</Button>
                                            </Tooltip>
                                        )},
                                    ]}
                                    rows={missing.items || []}
                                    rowKeyField="transaction_id"
                                    maxHeight={420}
                                />
                            </>
                        ) : <LoadingBlock rows={4} />}
                    </Stack>
                );
            case 5:
                return (
                    <Stack spacing={1.5}>
                        <SectionHeader
                            title="Period Reconciliation Report"
                            subtitle="Formal bank reconciliation report for a given period"
                            actions={report && (
                                <Button size="small" variant="outlined" startIcon={<DownloadIcon />}
                                    onClick={() => {
                                        const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
                                        const url = URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url; a.download = `reconciliation_${periodStart}_${periodEnd}.json`;
                                        a.click(); URL.revokeObjectURL(url);
                                    }}>Export</Button>
                            )}
                        />
                        <Stack direction="row" spacing={1}>
                            <TextField size="small" label="Start" type="date" InputLabelProps={{ shrink: true }}
                                value={periodStart} onChange={e => setPeriodStart(e.target.value)} />
                            <TextField size="small" label="End" type="date" InputLabelProps={{ shrink: true }}
                                value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} />
                            <Button size="small" variant="contained" disabled={!periodStart || !periodEnd || loading}
                                onClick={async () => {
                                    const r = await run(() => reconReport(periodStart, periodEnd));
                                    if (r?.data?.success) setReport(r.data.data);
                                }}>Generate</Button>
                        </Stack>
                        {report ? (
                            <>
                                <SummaryGrid cards={[
                                    { label: 'GL Debits', value: fmtMoney(report.gl_summary?.total_debit), accent: 'primary' },
                                    { label: 'GL Credits', value: fmtMoney(report.gl_summary?.total_credit), accent: 'primary' },
                                    { label: 'Bank Inflows', value: fmtMoney(report.bank_summary?.total_inflows), accent: 'success' },
                                    { label: 'Bank Outflows', value: fmtMoney(report.bank_summary?.total_outflows), accent: 'warning' },
                                ]} />
                                <Grid container spacing={1}>
                                    <Grid item xs={12} md={8}>
                                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                                            <Typography variant="caption" fontWeight={600}>Reconciliation Summary</Typography>
                                            {buildReportDonut().length > 0 && (
                                                <SimpleBarChart
                                                    data={buildReportDonut()}
                                                    xKey="name"
                                                    series={[{ key: 'value', label: 'Amount/Count', color: CHART_COLORS.primary }]}
                                                    height={220}
                                                    showLegend={false}
                                                    yFormatter={v => v >= 1000 ? `$${(v/1000).toFixed(0)}K` : v}
                                                />
                                            )}
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                                            <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mb: 1 }}>Status</Typography>
                                            <RadialGauge
                                                value={report.status === 'RECONCILED' ? 100 : 50}
                                                label={report.status}
                                                color={report.status === 'RECONCILED' ? CHART_COLORS.success : CHART_COLORS.warning}
                                            />
                                            <Typography variant="caption" sx={{ mt: 1, display: 'block' }} color="text.secondary">
                                                Unreconciled: {report.bank_summary?.unreconciled_count || 0}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                </Grid>
                            </>
                        ) : (
                            <EmptyState message="Pick a date range and click Generate." />
                        )}
                    </Stack>
                );
            default: return null;
        }
    };

    return (
        <AgentPageShell
            title="Bank Reconciliation Agent"
            subtitle="Agentic Transaction Intelligence — GL ↔ bank matching, timing differences, missing entries, period reports."
            tabs={TABS}
            activeTab={tab}
            onTabChange={setTab}
            error={error}
        >
            <Stack spacing={1.5}>
                <SummaryGrid cards={summaryCards} />
                <Divider />
                {renderTab()}
            </Stack>
        </AgentPageShell>
    );
}
